// Web Audio backend for the call tone subscriber.
//
// Production: WebAudioBackend wraps a singleton AudioContext, schedules
// ringback patterns and one-shot tones via gain automation in the audio
// thread (so background-tab throttling doesn't clip gudki).
//
// jsdom / unsupported environments: constructor catches, instance becomes
// a no-op fallback. Calls succeed silently, no crashes. Tests use
// MockAudioBackend directly via DI — this class isn't instantiated there.

import { pickAudioContextCtor } from './audioContext'

export type Cancel = () => void

export interface RingbackOpts {
  freq: number
  volume: number
  onMs: number
  offMs: number
}

export interface ToneOpts {
  freq: number
  durationMs: number
  volume: number
  attackMs?: number
  releaseMs?: number
}

export interface AudioBackend {
  /**
   * Schedules a repeating gated tone. Returns a cancel fn that fades out
   * within ~20ms and stops the oscillator. Backend renews the schedule
   * internally so calls > 60 seconds don't go silent.
   */
  playRingback(opts: RingbackOpts): Cancel

  /** Plays a single tone with attack/release envelope. Fire-and-forget. */
  playTone(opts: ToneOpts): void

  /** Cancels every active ringback. Idempotent. Used on unmount. */
  cancelAll(): void

  /** Removes user-gesture listeners. Idempotent. */
  dispose(): void
}

const FADE_SEC = 0.01           // 10ms attack/release on ringback edges
const CANCEL_FADE_SEC = 0.02    // 20ms ramp-to-zero on cancel
const SCHEDULE_AHEAD_SEC = 60   // keep at least this much audio scheduled
const EXTEND_INTERVAL_MS = 30000

const noop = (): void => {}

export class WebAudioBackend implements AudioBackend {
  private ctx: AudioContext | null = null
  private cancellers = new Set<Cancel>()
  private unlockHandler: (() => void) | null = null

  constructor() {
    try {
      const Ctor = pickAudioContextCtor()
      if (Ctor) {
        this.ctx = new Ctor()
        this.attachUnlockListeners()
      }
    } catch {
      this.ctx = null
    }
  }

  private attachUnlockListeners(): void {
    if (typeof document === 'undefined') return
    const handler = (): void => {
      this.ctx?.resume().catch(noop)
      this.detachUnlockListeners()
    }
    this.unlockHandler = handler
    // capture: true so we win against React's bubble-phase listeners and
    // unlock before the click handler kicks off the make_call round-trip.
    document.addEventListener('pointerdown', handler, { once: true, capture: true })
    document.addEventListener('keydown', handler, { once: true, capture: true })
  }

  private detachUnlockListeners(): void {
    if (typeof document === 'undefined' || !this.unlockHandler) return
    document.removeEventListener('pointerdown', this.unlockHandler, true)
    document.removeEventListener('keydown', this.unlockHandler, true)
    this.unlockHandler = null
  }

  private resumeIfNeeded(): void {
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(noop)
    }
  }

  playRingback(opts: RingbackOpts): Cancel {
    if (!this.ctx) return noop
    const ctx = this.ctx
    this.resumeIfNeeded()

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = opts.freq
    gain.gain.value = 0
    osc.connect(gain).connect(ctx.destination)

    const onSec = opts.onMs / 1000
    const offSec = opts.offMs / 1000
    const cycleSec = onSec + offSec
    const startAt = ctx.currentTime
    let nextCycleAt = startAt
    let cancelled = false

    const scheduleUntil = (until: number): void => {
      while (nextCycleAt < until) {
        const t = nextCycleAt
        gain.gain.setValueAtTime(0, t)
        gain.gain.linearRampToValueAtTime(opts.volume, t + FADE_SEC)
        gain.gain.setValueAtTime(opts.volume, t + onSec - FADE_SEC)
        gain.gain.linearRampToValueAtTime(0, t + onSec)
        nextCycleAt += cycleSec
      }
    }

    scheduleUntil(startAt + SCHEDULE_AHEAD_SEC)
    osc.start(startAt)

    let extendTimer: ReturnType<typeof setTimeout> | null = null
    const extend = (): void => {
      if (cancelled || !this.ctx) return
      scheduleUntil(this.ctx.currentTime + SCHEDULE_AHEAD_SEC)
      extendTimer = setTimeout(extend, EXTEND_INTERVAL_MS)
    }
    extendTimer = setTimeout(extend, EXTEND_INTERVAL_MS)

    const cancel: Cancel = () => {
      if (cancelled) return
      cancelled = true
      if (extendTimer) clearTimeout(extendTimer)
      this.cancellers.delete(cancel)
      const now = ctx.currentTime
      try {
        gain.gain.cancelScheduledValues(now)
        // Pin current gain value at `now` so the ramp starts from where
        // we are, not from whatever the schedule said. Without this the
        // ramp would jump to 0 first, then ramp - audible click.
        gain.gain.setValueAtTime(gain.gain.value, now)
        gain.gain.linearRampToValueAtTime(0, now + CANCEL_FADE_SEC)
        osc.stop(now + CANCEL_FADE_SEC + 0.005)
      } catch {
        // already stopped, ignore
      }
      osc.onended = () => {
        try { osc.disconnect() } catch { /* ignore */ }
        try { gain.disconnect() } catch { /* ignore */ }
      }
    }

    this.cancellers.add(cancel)
    return cancel
  }

  playTone(opts: ToneOpts): void {
    if (!this.ctx) return
    const ctx = this.ctx
    this.resumeIfNeeded()

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = opts.freq
    gain.gain.value = 0
    osc.connect(gain).connect(ctx.destination)

    const t = ctx.currentTime
    const attackSec = (opts.attackMs ?? 10) / 1000
    const releaseSec = (opts.releaseMs ?? 20) / 1000
    const durationSec = opts.durationMs / 1000
    const releaseStart = t + Math.max(durationSec - releaseSec, attackSec)

    gain.gain.setValueAtTime(0, t)
    gain.gain.linearRampToValueAtTime(opts.volume, t + attackSec)
    gain.gain.setValueAtTime(opts.volume, releaseStart)
    gain.gain.linearRampToValueAtTime(0, releaseStart + releaseSec)

    osc.start(t)
    osc.stop(t + durationSec + 0.01)
    osc.onended = () => {
      try { osc.disconnect() } catch { /* ignore */ }
      try { gain.disconnect() } catch { /* ignore */ }
    }
  }

  cancelAll(): void {
    const all = Array.from(this.cancellers)
    this.cancellers.clear()
    for (const c of all) {
      try { c() } catch { /* ignore */ }
    }
  }

  dispose(): void {
    this.cancelAll()
    this.detachUnlockListeners()
  }
}

let defaultBackend: AudioBackend | null = null

export function getDefaultBackend(): AudioBackend {
  if (!defaultBackend) defaultBackend = new WebAudioBackend()
  return defaultBackend
}

/** Test helper. Don't use in production code. */
export function __resetDefaultBackendForTests(): void {
  defaultBackend?.dispose()
  defaultBackend = null
}
